import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasCapability } from '@prooffleet/contracts';
import { ApiError, api, useSession } from '@prooffleet/client';
import { EmptyState, PageHeader } from '../components/Page.js';

interface PolicyPayload {
  policy: {
    _id: string;
    version: number;
    texts: Record<string, { title: string; body: string }>;
    effectiveFrom: string;
    graceDays: number;
    publishedAt: string;
  } | null;
}

/**
 * Регламент осмотров флота.
 *
 * Обязательность осмотра держится на внутренней политике компании, а не на
 * федеральном требовании. Юридически значима не сама обязательность, а полнота
 * журнала: объяснённый пропуск безопасен, молчаливый превращается в улику.
 *
 * Опубликованная редакция неизменяема. Новая редакция — новая версия и новое
 * подтверждение от каждого водителя, потому что подтверждение хранит язык и
 * SHA-256 показанного текста: подписывается не нажатие кнопки, а нажатие под
 * конкретными словами.
 */
export function Policy(): React.JSX.Element {
  const actor = useSession((s) => s.actor);
  const canWrite = actor !== null && hasCapability(actor, 'policy:write');
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [graceDays, setGraceDays] = useState<number | string>(7);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['policy'],
    queryFn: () => api<PolicyPayload>('/policy'),
    retry: false,
  });

  const current = data?.policy ?? null;

  const publish = useMutation({
    mutationFn: () =>
      api('/policy', {
        method: 'POST',
        body: {
          texts: { en: { title, body } },
          effectiveFrom: new Date().toISOString(),
          graceDays: Number(graceDays),
        },
      }),
    onSuccess: async () => {
      setOpen(false);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['policy'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Could not publish the policy');
    },
  });

  const startNew = (): void => {
    const en = current?.texts['en'];
    setTitle(en?.title ?? 'Pre-Trip Inspection Policy');
    setBody(en?.body ?? '');
    setGraceDays(current?.graceDays ?? 7);
    setError(null);
    setOpen(true);
  };

  return (
    <Stack gap={0}>
      <PageHeader
        title="Inspection policy"
        hint="The fleet's own rule, not a federal one. Drivers acknowledge the exact text they were shown, and a published revision can never be edited."
        action={
          canWrite ? (
            <Button onClick={startNew}>{current === null ? 'Publish policy' : 'New revision'}</Button>
          ) : undefined
        }
      />

      {isLoading && <Text c="dimmed">Loading…</Text>}

      {!isLoading && current === null && (
        <EmptyState
          title="No policy published"
          hint="Until a policy exists, a missed inspection has nothing to be measured against."
        />
      )}

      {current !== null && (
        <Stack gap="md">
          <Group gap={8} align="baseline">
            <Badge variant="light" color="gray">
              revision {current.version}
            </Badge>
            <Text className="pf-mono">
              in force since {new Date(current.effectiveFrom).toLocaleDateString()} · grace{' '}
              {current.graceDays} days
            </Text>
          </Group>

          {Object.entries(current.texts).map(([locale, text]) => (
            <div
              key={locale}
              style={{ borderTop: '1px solid var(--pf-line)', paddingTop: 14, marginTop: 4 }}
            >
              <Text className="pf-eyebrow" mb={4}>
                {locale}
              </Text>
              <Text fw={620} fz="md">
                {text.title}
              </Text>
              <Text fz="sm" c="gray.7" mt={6} style={{ whiteSpace: 'pre-wrap' }} maw="72ch">
                {text.body}
              </Text>
            </div>
          ))}
        </Stack>
      )}

      <Modal opened={open} onClose={() => setOpen(false)} title="Publish a revision" size="lg">
        <Stack gap="md">
          <Alert color="blue">
            Publishing does not edit the current revision. Every driver acknowledges the new text
            again, and the acknowledgement records the language and a hash of what they read.
          </Alert>

          <TextInput
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
          />
          <Textarea
            label="Text"
            autosize
            minRows={8}
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            placeholder="A pre-trip inspection is mandatory before every dispatch…"
          />
          <NumberInput
            label="Grace period, days"
            description="After this, a driver who has not acknowledged loses access. That is the fleet's decision, not a verdict from the app."
            value={graceDays}
            onChange={setGraceDays}
            min={0}
            max={90}
          />

          {error !== null && <Alert color="red">{error}</Alert>}

          <Button
            loading={publish.isPending}
            disabled={title.trim() === '' || body.trim() === ''}
            onClick={() => publish.mutate()}
          >
            Publish revision
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
